/**
 * awe-product-timer.js
 *
 * Custom element for the PDP countdown timer block.
 *
 * Reads start/end date/time from data attributes and ticks every second.
 * Stays hidden before the start time, shows the countdown to the end time
 * once started, and hides itself again once the end time passes — all
 * resolved client-side since Shopify caches rendered Liquid output and
 * "now" can't be evaluated reliably at render time.
 *
 * Data attributes (set by Liquid):
 *   data-start-date  — "YYYY-MM-DD" (optional; blank = active immediately)
 *   data-start-time  — "HH:MM" (24-hour, defaults to "00:00")
 *   data-end-date    — "YYYY-MM-DD" (required)
 *   data-end-time    — "HH:MM" (24-hour, defaults to "23:59")
 */

class AweProductTimerComponent extends HTMLElement {
  /** @type {number|null} */
  #intervalId = null;

  /** @type {Date|null} */
  #startDate = null;

  /** @type {Date|null} */
  #endDate = null;

  /** @type {{ days: Element|null, hours: Element|null, minutes: Element|null, seconds: Element|null }} */
  #els = {};

  connectedCallback() {
    const endDateStr = this.dataset.endDate;
    if (!endDateStr) return;

    this.#endDate = this.#parseDateTime(endDateStr, this.dataset.endTime || '23:59');

    const startDateStr = this.dataset.startDate;
    this.#startDate = startDateStr
      ? this.#parseDateTime(startDateStr, this.dataset.startTime || '00:00')
      : null;

    this.#els = {
      days: this.querySelector('[data-days]'),
      hours: this.querySelector('[data-hours]'),
      minutes: this.querySelector('[data-minutes]'),
      seconds: this.querySelector('[data-seconds]'),
    };

    this.#tick();
    this.#intervalId = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    if (this.#intervalId !== null) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  #parseDateTime(dateStr, timeStr) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hours, minutes] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, 0);
  }

  #tick() {
    const now = Date.now();

    if (this.#startDate && now < this.#startDate.getTime()) {
      this.style.display = 'none';
      return;
    }

    if (now >= this.#endDate.getTime()) {
      this.style.display = 'none';
      if (this.#intervalId !== null) {
        clearInterval(this.#intervalId);
        this.#intervalId = null;
      }
      return;
    }

    this.style.display = '';

    const distance = this.#endDate.getTime() - now;
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    const { days: dEl, hours: hEl, minutes: mEl, seconds: sEl } = this.#els;
    if (dEl) dEl.textContent = String(days).padStart(2, '0');
    if (hEl) hEl.textContent = String(hours).padStart(2, '0');
    if (mEl) mEl.textContent = String(minutes).padStart(2, '0');
    if (sEl) sEl.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('awe-product-timer-component')) {
  customElements.define('awe-product-timer-component', AweProductTimerComponent);
}
